<script setup lang="ts">
import type { EstimatedShippingRange, Price } from "mws-ts-v2-sdk";

import { BidAttemptType } from "~/types/Catalog";
import type {
  ProductCostsPrice,
  ProductPercentageCostsPrice,
  ShippingProductPercentageCostsPrice,
  VATProductPercentageCostsPrice,
} from "~/types/ProductCosts";

type Props = {
  buyersPremium: ProductPercentageCostsPrice;
  shipping: ShippingProductPercentageCostsPrice;
  vat: VATProductPercentageCostsPrice;
  total: ProductCostsPrice;
  bid?: ProductCostsPrice & {
    productName: string;
    eventName?: string | null;
    bidType?: BidAttemptType;
  };
  paymentFee?: ProductPercentageCostsPrice;
  estimatedDelivery?: {
    estimatedShippingRange: EstimatedShippingRange;
    isFrame: boolean;
  } | null;
  discount?: {
    discountPercentage: number;
    discountAmount: Price;
  };
  options: {
    priceVariant: "final" | "estimated" | "max";
    isInCheckout?: boolean;
    isOrderBundled?: boolean;
    isShippingCountryProvided: boolean;
  };
};

const { options, shipping, vat } = defineProps<Props>();

const { t } = useI18n();

const isBundlingInfoDialogOpen = ref(false);

const showAddAddressMessage = computed(() => {
  return !options.isShippingCountryProvided && options.priceVariant !== "final";
});

const shippingOffice = computed(() => {
  if (!shipping.officeName) return null;

  const shippingOfficeCountryCode = shipping.officeCountryCode
    ? `, (${shipping.officeCountryCode})`
    : "";

  return `${shipping.officeName}${shippingOfficeCountryCode}`;
});

const totalText = computed(() => {
  if (options.priceVariant === "estimated") {
    return t("product.costs.subtotal");
  }

  if (options.priceVariant === "max") {
    return t("product.costs.estimatedMaxTotal");
  }

  return t("product.costs.total");
});

const isSalesTax = useIsSalesTax(() => vat);
</script>

<template>
  <ul
    class="product-costs__list product-costs__list-subcosts text-sm"
    v-bind="$attrs"
  >
    <li
      v-if="bid?.costs?.currency"
      class="my-2"
      data-test="product-costs-bid"
    >
      <div class="font-semibold flex items-center justify-between w-full">
        <p>
          {{
            bid?.bidType === BidAttemptType.Maxbid
              ? $t("product.bidAmount.yourMaxBid")
              : $t("product.bidAmount.yourBid")
          }}
        </p>
        <Transition
          key="bid-cost-transition"
          name="fade"
          mode="out-in"
        >
          <BaseCurrency
            :key="bid.costs.price"
            :show-decimals="true"
            :value="bid.costs.price"
            :iso="bid.costs.currency"
          />
        </Transition>
      </div>
      <p class="mt-1 text-xs text-platform-text-3">
        {{ bid.productName }}
      </p>
      <p
        v-if="bid.eventName"
        class="mt-1 text-xs text-platform-text-3"
      >
        {{ bid.eventName }}
      </p>
    </li>
    <li
      v-if="discount"
      class="my-2"
    >
      <div
        class="flex items-center justify-between w-full text-platform-success"
      >
        {{
          $t("product.costs.discountApplied", {
            PLACEHOLDER_percentage: discount.discountPercentage,
          })
        }}
        <BaseCurrency
          v-if="discount.discountAmount.currency"
          :show-decimals="true"
          :value="-discount.discountAmount.price"
          :iso="discount.discountAmount.currency"
        />
      </div>
    </li>
    <li
      v-if="shipping"
      data-test="product-costs-shipping"
      class="my-2"
    >
      <div class="flex items-center justify-between w-full">
        <p v-if="options.priceVariant === 'final'">
          {{ $t("product.costs.shippingCosts") }}
        </p>
        <p v-else>{{ $t("product.costs.estimatedShippingCosts") }}</p>
        <Transition
          key="shipping-cost-transition"
          name="fade"
          mode="out-in"
        >
          <BaseCurrency
            v-if="shipping.costs?.currency"
            :key="shipping.costs.price"
            data-test="shipping-cost-price"
            :show-decimals="true"
            :value="shipping.costs.price"
            :iso="shipping.costs.currency"
          />
          <span
            v-else-if="showAddAddressMessage"
            class="text-platform-text-3"
            data-test="product-costs-no-address-placeholder"
          >
            {{ t("product.costs.noAddressPlaceholder") }}
          </span>
        </Transition>
      </div>

      <div
        v-if="options.isOrderBundled"
        class="flex flex-row gap-2 text-platform-text-2 mt-1"
      >
        <p
          class="text-xs"
          data-test="product-costs-bundled-message"
        >
          {{ $t("product.costs.orderBundled") }}
        </p>
        <button @click="() => (isBundlingInfoDialogOpen = true)">
          <BaseIcon
            name="circleInfo"
            :size="14"
          />
        </button>
        <ProductCostsBundlingInfoDialog v-model="isBundlingInfoDialogOpen" />
      </div>

      <p
        v-if="shippingOffice"
        class="text-xs text-platform-text-2 mt-1"
      >
        {{
          $t("product.costs.shippedFrom", {
            PLACEHOLDER_officeName: shippingOffice,
          })
        }}
      </p>

      <ProductShippingEstimatedDelivery
        v-if="estimatedDelivery"
        :estimated-shipping-range="estimatedDelivery.estimatedShippingRange"
        :is-frame="estimatedDelivery.isFrame"
        class="text-xs text-platform-text-2 mt-1"
      />
    </li>
    <li
      v-if="buyersPremium?.costs?.currency && buyersPremium.costs.price > 0"
      class="flex items-center justify-between my-2 w-full"
      data-test="product-costs-buyers-premium"
    >
      <p class="flex gap-1">
        {{ $t("product.costs.buyersPremium") }}
        <span
          v-if="buyersPremium.percentage !== null"
          data-test="product-costs-buyers-premium-percentage"
        >
          ({{ buyersPremium.percentage }}%)
        </span>
        <BaseTooltip class="ml-1">
          <p>{{ $t("product.costs.buyersPremiumInfo") }}</p>
        </BaseTooltip>
      </p>
      <Transition
        key="buyers-premium-transition"
        name="fade"
        mode="out-in"
      >
        <BaseCurrency
          :key="buyersPremium.costs.price"
          :show-decimals="true"
          :value="buyersPremium.costs.price"
          :iso="buyersPremium.costs.currency"
        />
      </Transition>
    </li>

    <li
      v-if="paymentFee?.costs?.currency"
      class="flex items-center justify-between my-2 w-full"
      data-test="product-costs-payment"
    >
      <p>{{ $t("product.costs.paymentFee") }}</p>
      <Transition
        key="payment-fee-transition"
        name="fade"
        mode="out-in"
      >
        <BaseCurrency
          :key="paymentFee.costs.price"
          :show-decimals="true"
          :value="paymentFee.costs.price"
          :iso="paymentFee.costs.currency"
        />
      </Transition>
    </li>
    <ProductCostsSalesTaxInfo
      v-if="isSalesTax"
      :costs="vat.costs"
      :percentage="vat.percentage"
      :is-final-price="options.priceVariant === 'final'"
    />
    <ProductCostsVATInfo
      v-else
      :costs="vat.costs"
      :percentage="vat.percentage"
      :item-vat-explanation="vat?.itemVatExplanation"
      :handling-vat-explanation="vat?.handlingVatExplanation"
      :vat-number-check-status="vat?.vatNumberCheckStatus"
    />
    <li
      v-if="total?.costs?.currency"
      class="product-costs__list-item product-costs__list-item--subtotal font-semibold mt-3 pt-3 mb-0 flex justify-between border-t"
      data-test="product-costs-total"
    >
      <p>{{ totalText }}</p>
      <Transition
        key="total-cost-transition"
        name="fade"
        mode="out-in"
      >
        <div>
          <BaseCurrency
            :key="total.costs.price"
            :show-decimals="true"
            :value="total.costs.price"
            :iso="total.costs.currency"
            currency-size="xs"
          />
        </div>
      </Transition>
    </li>
  </ul>
  <div class="mt-2 text-xs">
    <p
      v-if="options.priceVariant !== 'final'"
      class="text-platform-text-3 mt-1"
      data-test="product-costs-final-price-calculation"
    >
      {{ $t("product.costs.finalPriceCalculation") }}
    </p>
    <p
      v-if="vat?.costs?.price === 0 && !isSalesTax"
      class="text-platform-text-3 mt-1 font-semibold"
      data-test="product-costs-tax-message"
    >
      {{
        options.isInCheckout
          ? $t("product.costs.taxesAppliedCheckout")
          : $t("product.costs.taxesApplied")
      }}
    </p>
  </div>
</template>

<style lang="postcss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
